/**
 * LoginPage Component
 *
 * Authentication page using synapse design system components.
 * Supports adding additional accounts via ?mode=addAccount query param.
 */

import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Input, Label, Card, CardContent, useTheme } from '@lognai/synapse-design-system-v2';
import { useAuth } from '../../hooks/useAuth';
import { isTwoFactorChallenge } from '../../services/api/auth';
import { accountManager } from '../../utils/accountManager';
import { Logo } from '../../components/Logo';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoggingIn, loginError, completeTwoFactor, isVerifyingTwoFactor } = useAuth();
  const { theme } = useTheme();

  const isAddAccountMode = searchParams.get('mode') === 'addAccount';
  const existingAccountCount = accountManager.getAccountCount();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  /*
    The second step, when the account has two-factor on. Holding the ticket in
    state rather than navigating to another route keeps the half-finished
    sign-in out of history and out of any URL — a challenge token in a browser's
    back stack is a credential left lying around.
  */
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    try {
      const response = await login({ email, password });

      if (response.success && response.data) {
        if (isTwoFactorChallenge(response.data)) {
          setChallengeToken(response.data.challengeToken);
          return;
        }
        navigate('/workspace');
      } else {
        // API returned error in response
        setFormError(response.error || 'Login failed');
      }
    } catch (err) {
      // Network or other error
      console.error('Login failed:', err);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!challengeToken) return;

    try {
      const response = await completeTwoFactor({ challengeToken, code: code.trim() });
      if (response.success && response.data && !isTwoFactorChallenge(response.data)) {
        navigate('/workspace');
        return;
      }
      setFormError(response.error || 'Could not verify that code');
    } catch (err) {
      console.error('Two-factor verification failed:', err);
      setFormError('Could not verify that code');
    }
  };

  /** Back to the password step, discarding the ticket rather than parking it. */
  const handleStartOver = () => {
    setChallengeToken(null);
    setCode('');
    setPassword('');
    setFormError(null);
  };

  const handleCancel = () => {
    // Go back to previous page
    navigate(-1);
  };

  return (
    <Card className="w-full">
      <div className="space-y-3 px-5 pt-6 pb-5 text-center">
        <div className="flex justify-center">
          {/* The auth Card is a surface, so it flips with the theme — the logo's
              surface-aware elements must follow it. A fixed `dark` variant painted
              them #0f172a on the dark Card, which is the app's default theme on a
              fresh browser, leaving the wordmark's mid-section near-invisible. */}
          <Logo variant={theme === 'light' ? 'dark' : 'white'} className="h-10 w-auto" />
        </div>
        <h1 className="text-xl font-normal text-foreground">
          {challengeToken
            ? 'Two-step verification'
            : isAddAccountMode
              ? 'Add Another Account'
              : 'Sign in to your account'}
        </h1>
        {challengeToken && (
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app.
          </p>
        )}
        {isAddAccountMode && (
          <p className="text-sm text-muted-foreground">
            Sign in with a different account. You have {existingAccountCount} account{existingAccountCount !== 1 ? 's' : ''} already signed in.
          </p>
        )}
      </div>

      <CardContent>
        {challengeToken ? (
          /* The second step. The password fields are gone rather than
             disabled — leaving a filled password box on screen invites a
             browser or a passer-by to make something of it, and there is
             nothing left to do with it. */
          <form method="post" onSubmit={handleVerify} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="totp-code">Verification code</Label>
              <Input
                id="totp-code"
                /* `text` with a numeric inputMode, not `type="number"`: a
                   number input strips leading zeros, and a TOTP code beginning
                   0 is one in ten. */
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                maxLength={11}
                autoFocus
                required
              />
              <p className="text-xs text-muted-foreground">
                Lost your device? Enter one of your backup codes instead.
              </p>
            </div>

            {(formError || loginError) && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {formError || loginError?.message || 'Could not verify that code'}
              </div>
            )}

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={handleStartOver}>
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isVerifyingTwoFactor || code.trim().length === 0}
              >
                {isVerifyingTwoFactor ? 'Verifying…' : 'Verify'}
              </Button>
            </div>
          </form>
        ) : (
          <>
          {/* method="post" is belt-and-braces: handleSubmit calls preventDefault so
              a browser never issues the request, but a non-JS client / scanner that
              submits the form must use POST, not the default GET that puts the
              password in the URL (Qualys WAS 153006). */}
          <form method="post" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                autoComplete="username"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {!isAddAccountMode && (
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                required
              />
            </div>

            {(formError || loginError) && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {formError || loginError?.message || 'Login failed'}
              </div>
            )}

            <div className="flex gap-3">
              {isAddAccountMode && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className={isAddAccountMode ? 'flex-1' : 'w-full'}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Signing in...' : isAddAccountMode ? 'Add Account' : 'Sign In'}
              </Button>
            </div>
          </form>
          </>
        )}
      </CardContent>
    </Card>
  );
}
